/**
 * What it takes to kill you — effective HP, per element.
 *
 * The offensive half of this engine already flies a hit through fourteen layers and asks the
 * *target's* sheet what happens to it. Defence is the same walk with the sheets swapped: the
 * character is the target, the attacker is the mob `config.enemy` describes, and the question is
 * what fraction of a raw incoming hit reaches the pools.
 *
 * So nothing here re-derives mitigation. `sweep` runs the same `armor_mitigation`,
 * `physical_mitigation`, `elemental_mitigation`, `damage_reduction`, `damage_suppression`,
 * `damage_block` and `flat_damage_reduction` layers the offensive pass does, off the same stat
 * effects. Only the pools and the avoidance averaging are new.
 *
 * ## The pools
 *
 * `magic_shield` absorbs first, and it is not simply added to health:
 *
 *     float bypassing = getChaosDamageBypassingShield(effect, info, dmg);
 *     float dmgReduced = Mth.clamp(dmg - bypassing, 0, current);
 *     ...
 *     return dmg - dmgReduced;
 *
 * — MagicShield.modifyEntityDamage, with `CHAOS_BYPASS_PERCENT = 50`. Half of chaos damage walks
 * past the shield straight into health, unless `chaos_doesnt_bypass_magic_shield` is above zero.
 * That makes the chaos pool `min(health + shield, 2 × health)` rather than `health + shield`: once
 * your shield is bigger than your health, the bypassing half kills you before the shield empties.
 *
 * ## What is deliberately not folded in
 *
 * **Mana absorption.** `DamageAbsorbedByMana` sends a percentage of each hit to mana, but only
 * while mana is above half its maximum and only down to that half:
 *
 *     if (currentMana / maxMana > 0.5F) {
 *         float dmgReduced = Mth.clamp(dmg * data.getValue() / 100F, 0, currentMana - (maxMana * 0.5F));
 *
 * so it is a buffer of `maxMana / 2` that drains at a fixed rate, not a pool. It is reported with
 * its two numbers rather than folded into one, because folding it in would mean inventing a
 * regeneration model to say how full the mana was when the hit landed.
 *
 * **Regeneration, and the second hit.** This is one hit against a character at full. Recovery
 * between hits is a different question and needs an incoming-damage rate, which nothing in a build
 * document states. `resources.ts` says how fast each pool refills between them.
 *
 * **Crit, and the attacker's uniform damage increases.** Effective HP answers "how much of the
 * mob's *base* hit do I survive". A crit, or a mob whose `dmg_multi` is 2, or a map tier's
 * `MORE total_damage`, makes every hit bigger alike — it does not make one of your defences
 * worse than another — so folding it in would be answering the over-time question twice. The
 * over-time figures do fold `total_damage` in, through `takenFromSwing`; crit stays out of both.
 *
 * Damage on hits **of one element** is the exception, and is in: see {@link attackerSheet}.
 *
 * ## The attacker
 *
 * Not a bare zero any more. `config.enemy.offence` carries what the mob hits with, and everything
 * on it that changes *mitigation* is swept from the Source side exactly as a player's would be:
 * accuracy, which is subtracted from your dodge and your spell dodge, and penetration, which is
 * taken off armour and off the raw resist before its clamp.
 *
 * `buildTargetEnemy` fills it the way the Training Dummy's presets do — except that the dummy
 * pins no numbers at all and lets Mine and Slash build the block, which is exactly the thing this
 * file could not do and therefore got wrong. A mob's offence is **two** sources, not one:
 * `MobStatUtils.getMobBaseStats` scales 1 accuracy to its level, and the pack's
 * `mmorpg_base_stats/mob` — which `CommonStatUtils.addBaseStats` gives every non-player entity —
 * adds 8 more accuracy and 6 armour penetration on the same curve. Reading only the first made
 * every mob nine times less accurate and infinitely less penetrating than the game's.
 *
 * Its **affixes**, and its map's, reach this sheet too; see {@link attackerAffixes} for which of
 * them do and why the uniform damage increases deliberately do not.
 */
import { SINGLE_ELEMENTS, baseGearType, isDualWielding, isTwoHanded, targetPreset, wornItems, } from "@cte2/schema";
import { balance } from "../balance.js";
import { resolveEffects } from "../calculate.js";
import { ORIGINAL_MODE } from "../compat.js";
import { statIndex } from "../stat-def.js";
import { EVENT, DamageEventState } from "./event.js";
import { layerIndex } from "./layers.js";
import { Recorder } from "./breakdown.js";
import { mobAffixMods } from "./mob-affixes.js";
import { mapMobMods } from "./map.js";
import { aggregateSpread, applySheetMods, sweep } from "./simulate.js";
import { mobAttackRate, mobHitSize } from "./incoming.js";
import { applyAilments } from "./ailments.js";
import { resolveProcs } from "./procs.js";
import { resources } from "./resources.js";
import { selfSustain } from "./self-sustain.js";
/**
 * Effective HP, per element.
 *
 * One hit, against a character at full, from the attacker `config.enemy` describes. The two
 * things a mob brings that change *mitigation* — accuracy, which is subtracted from your dodge,
 * and penetration, which comes off armour and off the raw resist before its clamp — are swept
 * from the Source side exactly as a player's would be. Everything else a mob has belongs to how
 * big the hit is rather than to how much of it you stop, and is reported beside the figure
 * instead of folded into it.
 *
 * With an empty `config.enemy.offence` that leaves a bare attacker, and these figures are then
 * the optimistic end: your own resists and armour at face value. A target preset fills it from
 * `MobStatUtils`, which gives a mob accuracy and nothing else.
 */
export function defence(build, snapshot, options = {}) {
    const diagnostics = [];
    const index = statIndex(snapshot);
    const layers = layerIndex(snapshot);
    const bal = balance(snapshot, options.balanceId);
    const compat = options.compat ?? ORIGINAL_MODE;
    const engineOptions = {
        ...(options.balanceId === undefined ? {} : { balanceId: options.balanceId }),
        ...(options.baseStatsId === undefined ? {} : { baseStatsId: options.baseStatsId }),
        ...(options.newbieResists === undefined ? {} : { newbieResists: options.newbieResists }),
    };
    const run = options.sheet ?? resolveEffects(build, snapshot, engineOptions);
    const sheet = run.stats;
    const health = sheet.get("health")?.value ?? 0;
    const magicShield = sheet.get("magic_shield")?.value ?? 0;
    // `DamageAbsorbedByMana.GUID` is `mana_shield` — the class name is not the stat id, and
    // there has never been a stat called `damage_absorbed_by_mana`. Reading that name returned
    // `undefined` on every real sheet, so the buffer below and its diagnostic never once fired,
    // including for a build wearing the Mana Battery keystone. Confirmed against the static
    // initialiser in `Mine_and_Slash-1.20.1-6.4.13.jar`; `code-only-stats.generated.ts` had it
    // right all along.
    const manaPercent = sheet.get("mana_shield")?.value ?? 0;
    const mana = sheet.get("mana")?.value ?? 0;
    const pools = {
        health,
        magicShield,
        manaAbsorb: { percent: manaPercent, buffer: manaPercent > 0 ? mana / 2 : 0 },
    };
    const hitSize = options.hitSize ?? Math.max(1, health + magicShield);
    // The curves for armour and dodge are read at the *attacker's* level, so an incoming hit needs
    // one. The enemy block is where a document states who it is fighting.
    const attackerLevel = build.config?.enemy?.level ?? build.config?.enemyLevel ?? build.character.level;
    // `chaos_doesnt_bypass_magic_shield` switches the bypass off entirely.
    const shieldHolds = (sheet.get("chaos_doesnt_bypass_magic_shield")?.value ?? 0) > 0;
    const hasShield = wearsShield(build, snapshot);
    const offence = build.config?.enemy?.offence ?? {};
    const attacker = attackerSheet(offence);
    const carried = attackerAffixes(build, snapshot, index, bal, attackerLevel, attacker, diagnostics);
    // The same attacker with its uniform damage increases too — only the over-time figures read it.
    const swinging = swingingSheet(attacker, offence, carried.sizing, index, diagnostics);
    // One shared sink across the element sweeps, deduplicated afterwards by {@link oncePerBlock}.
    //
    // A `proc_spell` block on a Target-side stat is mostly not element-specific —
    // `proc_quake_when_hit` fires on any hit you take — so every element's sweep reaches the same
    // block and pushes the same hit. `resolveProcs` *sums* what it is handed rather than deduping
    // it, so passing the raw sink reported a 50% proc at 250%: five elements' worth of one proc.
    const procHits = [];
    const restoreHits = [];
    const ailmentHits = [];
    const byElement = SINGLE_ELEMENTS.map((element) => {
        const input = {
            snapshot,
            index,
            layers,
            balance: bal,
            compat,
            build,
            sheet,
            effects: run.effects,
            element: element.name,
            hitSize,
            attackerLevel,
            attacker,
            diagnostics,
            hasShield,
            procs: procHits,
            restores: restoreHits,
            ailments: ailmentHits,
        };
        const { taken, steps, arrivesAs } = takenFraction(input);
        // Two sweeps per element rather than one, because the two questions have different answers
        // and neither can be derived from the other: the avoidance layers sit in the middle of the
        // chain, so their contribution is not a factor that can be divided back out afterwards.
        // Ten sweeps of fourteen layers is a fraction of a millisecond.
        // No sinks on this one: it is the same event swept a second time with avoidance forced off,
        // so collecting from it would count every proc and every restore twice.
        const { procs: _procs, restores: _restores, ailments: _ailments, ...withoutSinks } = input;
        const unavoided = takenFraction({ ...withoutSinks, noAvoidance: true, diagnostics: [] });
        const fromSwing = swinging === attacker ? taken : takenFraction({ ...withoutSinks, attacker: swinging, diagnostics: [] }).taken;
        const pool = poolFor(element.guid, health, magicShield, shieldHolds);
        return {
            element: element.name,
            taken,
            takenUnavoided: unavoided.taken,
            takenFromSwing: fromSwing,
            pool,
            effectiveHealth: taken > 0 ? pool / taken : Number.POSITIVE_INFINITY,
            maximumHit: unavoided.taken > 0 ? pool / unavoided.taken : Number.POSITIVE_INFINITY,
            steps,
            unavoidedSteps: unavoided.steps,
            arrivesAs,
        };
    });
    const weakest = byElement.reduce((worst, entry) => entry.effectiveHealth < worst.effectiveHealth ? entry : worst);
    const mostFragile = byElement.reduce((worst, entry) => entry.maximumHit < worst.maximumHit ? entry : worst);
    // An element you take nothing from adds 0 here (1 / Infinity), which is what it is worth.
    const inverseSum = byElement.reduce((sum, entry) => sum + 1 / entry.effectiveHealth, 0);
    const averageEffectiveHealth = inverseSum > 0 ? byElement.length / inverseSum : Number.POSITIVE_INFINITY;
    if (pools.manaAbsorb.percent > 0) {
        diagnostics.push({
            severity: "info",
            code: "mana-absorb-not-in-ehp",
            path: "config",
            message: `\`mana_shield\` sends ${pools.manaAbsorb.percent.toFixed(1)}% of every hit to ` +
                `mana, but only while mana is above half its maximum, so it is a buffer of ` +
                `${Math.round(pools.manaAbsorb.buffer)}, not a pool. How full that buffer is when a hit ` +
                `lands depends on regeneration between hits. A document that states how hard and how ` +
                `often this enemy hits gets that answer as a number of seconds on the over-time figures; ` +
                `either way it is reported beside the effective HP rather than added to it, because the ` +
                `figure above is one hit against a character at full.`,
        });
    }
    const blockChance = sheet.get("block_chance")?.value ?? 0;
    if (blockChance > 0 && !hasShield) {
        diagnostics.push({
            severity: "info",
            code: "block-needs-shield",
            path: "gear",
            message: `\`block_chance\` is ${blockChance.toFixed(1)}%, but \`BlockChance\` refuses to fire ` +
                `without a shield in the offhand (\`canActivate\` tests the offhand stack with ` +
                `\`instanceof ShieldItem\`), and this build has none. The chance is on the sheet and stops ` +
                `nothing, so it is left out of the figures below rather than quietly counted.`,
        });
    }
    const pierces = (offence.armorPenetration ?? 0) > 0 ||
        Object.values(offence.penetration ?? {}).some((v) => v > 0);
    diagnostics.push({
        severity: "info",
        code: "ehp-attacker",
        path: "config.enemy.offence",
        message: `Effective HP is measured against a level ${attackerLevel} attacker with ` +
            `${offence.accuracy === undefined ? "no accuracy" : `${round(offence.accuracy)} accuracy`}` +
            `${pierces ? " and the penetration on `config.enemy.offence`" : " and no penetration"}` +
            `${carried.applied.length === 0 ? "" : `, carrying ${carried.applied.join(", ")} from its affixes`}. ` +
            `A target preset fills those from \`MobStatUtils\` and the pack's \`mmorpg_base_stats/mob\`, ` +
            `which between them give a mob accuracy, armour penetration and 50% more damage on ` +
            `elemental and chaos hits. That last one is in these figures, because it changes which resist ` +
            `matters. Crit and the attacker's uniform damage increases (\`total_damage\`, a map tier) ` +
            `are not, since they make every hit bigger alike; the over-time figures carry \`total_damage\`.` +
            `${carried.withheld.length === 0 ? "" : ` Its ${carried.withheld.join(", ")} is real and is left out of both for that reason.`}`,
    });
    const overTime = overTimeFor({
        build,
        snapshot,
        options,
        offence,
        attackerLevel,
        byElement,
        pools,
        sheet,
        effects: run.effects,
        procHits: oncePerBlock(procHits),
        restoreHits: oncePerRestore(restoreHits),
        ailmentHits: oncePerAilment(ailmentHits),
        diagnostics,
    });
    return {
        pools,
        hitSize,
        attackerLevel,
        byElement,
        weakest,
        averageEffectiveHealth,
        mostFragile,
        ...(overTime === undefined ? {} : { overTime }),
        diagnostics,
    };
}
/**
 * The second hit, and every one after it.
 *
 * Nothing here re-derives mitigation: `byElement[e].taken` is the fraction that already survived
 * the full fourteen-layer sweep, and this multiplies it by a hit size and a clock. The sustain
 * walk is `selfSustain`, which was written for Holy Fire's recoil and asks exactly this question
 * — shield regeneration pays first because the shield is hit first, then health regeneration,
 * and what neither covers is the rate your bar actually falls at.
 *
 * Returns `undefined` unless the document states both halves of the attacker's hit. That is the
 * property the whole feature rests on: an unstated hit is the state every existing document,
 * capture and fixture is in, so none of their numbers can move.
 */
/**
 * One entry per proc block, at the highest chance any element's sweep gave it.
 *
 * The highest rather than the first or the mean, because an element-gated block — one carrying a
 * `<element>_damage_taken` style `ifs` — is reached at its real chance on the element it applies
 * to and at zero everywhere else. Taking the maximum is what reports that proc at the chance it
 * actually has when it fires, rather than at a fifth of it.
 */
function oncePerBlock(hits) {
    const best = new Map();
    for (const hit of hits) {
        const key = `${hit.statId}:${hit.spellId}:${hit.side}`;
        const seen = best.get(key);
        if (seen === undefined || hit.chance > seen.chance)
            best.set(key, hit);
    }
    return [...best.values()];
}
/** The same, for restores: one per stat and pool, at the largest amount any element produced. */
function oncePerRestore(records) {
    const best = new Map();
    for (const record of records) {
        const key = `${record.statId}:${record.effectId}:${record.resource}`;
        const seen = best.get(key);
        if (seen === undefined || record.amount > seen.amount)
            best.set(key, record);
    }
    return [...best.values()];
}
/**
 * One entry per ailment, at the highest chance any element's sweep produced.
 *
 * Same reason as {@link oncePerBlock}: each element is swept separately, and an ailment is gated
 * on its own element, so bleed is rolled on the physical sweep and reads zero on the other four.
 * The maximum is the chance it has when it can happen at all.
 */
function oncePerAilment(results) {
    const best = new Map();
    for (const result of results) {
        const seen = best.get(result.ailment);
        if (seen === undefined || result.chance > seen.chance)
            best.set(result.ailment, result);
    }
    return [...best.values()].filter((result) => result.chance > 0);
}
function overTimeFor(input) {
    const { build, snapshot, offence, attackerLevel, byElement, pools, diagnostics } = input;
    const hit = mobHitSize(snapshot, offence, attackerLevel, input.options.compat ?? ORIGINAL_MODE, targetPreset(build.config?.targetPreset ?? "")?.rarityId);
    const ratePerSecond = mobAttackRate(offence);
    if (hit === undefined || ratePerSecond === undefined) {
        // Said once, at the point it matters, rather than left as a blank card. Both halves are
        // named because filling in one of them alone still produces nothing.
        diagnostics.push({
            severity: "info",
            code: "incoming-hit-unstated",
            path: "config.enemy.offence",
            message: "How hard and how often this enemy hits is not stated, so nothing here says how long you " +
                "survive it, only how big a single hit you could take from full. The two fields are the " +
                "mob's Minecraft attack damage and how often it swings; neither is derivable, because " +
                "`mmorpg_entity` carries no attack damage and `MobStatUtils` gives a mob accuracy and " +
                "nothing else. An attacker profile on the Config tab fills both.",
        });
        return undefined;
    }
    const regen = resources(build, snapshot, {
        ...(input.options.balanceId === undefined ? {} : { balanceId: input.options.balanceId }),
        ...(input.options.sheet === undefined ? {} : { sheet: input.options.sheet }),
    });
    const perSecondOf = (id) => regen.byResource.find((r) => r.resource === id)?.inCombatPerSecond ?? 0;
    const healthRegen = perSecondOf("health");
    const shieldRegen = perSecondOf("magic_shield");
    const elements = byElement.map((entry) => {
        const perHit = entry.takenFromSwing * hit.raw;
        const perSecond = perHit * ratePerSecond;
        const sustain = selfSustain({
            perSecond,
            perCast: perHit,
            magicShield: { max: pools.magicShield, perSecond: shieldRegen },
            health: { max: pools.health, perSecond: healthRegen },
        });
        return {
            element: entry.element,
            perHit,
            perSecond,
            sustain,
            hitsSurvived: sustain.secondsToDeath * ratePerSecond,
        };
    });
    const deadliest = elements.reduce((worst, e) => e.sustain.secondsToDeath < worst.sustain.secondsToDeath ? e : worst);
    // `mana_shield` sends a percent of each hit to mana, but only while mana is above half its
    // maximum — so it is a buffer of `maxMana / 2` draining at a fixed rate, and with a rate there
    // is finally a number of seconds to put on it. The one-hit figures still leave it out.
    let manaAbsorbSeconds;
    if (pools.manaAbsorb.percent > 0) {
        const drain = (deadliest.perSecond * pools.manaAbsorb.percent) / 100;
        const net = drain - perSecondOf("mana");
        manaAbsorbSeconds = net > 0 ? pools.manaAbsorb.buffer / net : Number.POSITIVE_INFINITY;
    }
    // The chances are the sweep's; only the rate is this file's. `damageOf` is deliberately a
    // constant zero here: what a defensive proc *casts* is an ordinary spell whose damage belongs
    // to the Damage tab, and resolving it would mean running the whole spell pipeline from inside
    // the defence pass. The rate, the chance and the cooldown ceiling are what was missing.
    const procs = resolveProcs({
        snapshot,
        build,
        effects: input.effects,
        onHit: input.procHits,
        onCrit: [],
        critChance: 0,
        hitsPerSecond: 0,
        incomingPerSecond: ratePerSecond,
        sheet: input.sheet,
        spellTags: new Set(),
        damageOf: () => 0,
        diagnostics: [],
    })
        .filter((proc) => proc.limit === undefined || proc.limit === "when-hit" || proc.limit === "no-damage")
        .map((proc) => {
        // `damageOf` is a constant zero here by choice, so `resolveProcs` concludes "no-damage" —
        // which would be a real finding on the Damage tab and is an artefact of this call. The
        // rate and the chance are what this pass is for; the spell's damage is the other tab's.
        if (proc.limit !== "no-damage")
            return proc;
        const { limit: _dropped, ...rated } = proc;
        return rated;
    });
    const restoresPerSecond = input.restoreHits.map((record) => ({
        ...record,
        amount: record.amount * ratePerSecond,
    }));
    return {
        ratePerSecond,
        rawPerHit: hit.raw,
        hit,
        byElement: elements,
        deadliest,
        ...(manaAbsorbSeconds === undefined ? {} : { manaAbsorbSeconds }),
        procs,
        restoresPerSecond,
        ailments: [...input.ailmentHits],
    };
}
/**
 * The attacker's stats, as a sheet the sweep can read.
 *
 * What changes *which of your defences a hit meets, and how hard*: accuracy, penetration, and the
 * mob's damage on hits **of one element** — `all_elemental_damage 50` and `all_chaos_damage 50`
 * from the pack's `mmorpg_base_stats/mob`. That last pair is element-specific the way a
 * conversion is, so it belongs here with conversion: a mob's fire hit is half again its
 * physical one, and a character leaning on armour while their fire resist is low is weaker to
 * that mob than a flat reading of the two resists suggests.
 *
 * `total_damage` and the crit pair stay off. They make every hit bigger alike, which is not a
 * defence getting worse — see {@link swingingSheet} for where `total_damage` does go.
 */
function attackerSheet(offence) {
    const sheet = new Map();
    const put = (id, value) => {
        if (value === undefined || value === 0)
            return;
        sheet.set(id, { value, dmgMulti: 1, hardcap: 0, softcap: 0 });
    };
    put("accuracy", offence.accuracy);
    put("armor_penetration", offence.armorPenetration);
    for (const [guid, value] of Object.entries(offence.penetration ?? {})) {
        put(`${guid}_penetration`, value);
    }
    // `all_elemental_damage` is a real stat rather than an aggregate to spread: its effect is gated
    // on `ele_match_stat`, and `Elemental` matches fire, cold and lightning there.
    for (const [guid, value] of Object.entries(offence.elementDamage ?? {})) {
        put(`all_${guid}_damage`, value);
    }
    return sheet;
}
/**
 * The attacker again, with the increases that make **every** hit bigger — what it actually swings.
 *
 * `total_damage` from `savage`, from `offence.totalDamage` (`EntityConfig.dmg_multi`, a `MORE` in
 * `MobStatUtils.getMobConfigStats`), and from a map tier. Returns the same sheet when there are
 * none, so a caller can skip the extra sweep by identity.
 */
function swingingSheet(attacker, offence, sizing, index, diagnostics) {
    const mods = [...sizing];
    if (offence.totalDamage !== undefined && offence.totalDamage !== 0) {
        mods.push({
            statId: "total_damage",
            type: "MORE",
            value: offence.totalDamage,
            source: "dmg_multi",
            path: "config.enemy.offence.totalDamage",
        });
    }
    if (mods.length === 0)
        return attacker;
    const sheet = new Map(attacker);
    applySheetMods(sheet, mods, index, (severity, code, path, message) => diagnostics.push({ severity, code, path, message }));
    return sheet;
}
/**
 * The mob's own affixes and its map's, folded onto the sheet it hits you with.
 *
 * `config.enemy.affixes` already described the target when *you* attack it; this is the other
 * direction, and it was missing entirely — an enemy could carry Fire Lord and every effective-HP
 * figure would be the same as against a bare mob. It should not be: `fire_lord` is
 * `phys_to_fire 75` and `plus_phys_to_fire 50`, so three quarters of its physical hit arrives as
 * fire and half again on top of that, and which of your resists is doing the work changes
 * completely.
 *
 * ## What is let through, and what is held back
 *
 * The rule is the one `attackerSheet` already states: a stat that changes **what the hit is made
 * of, or what it walks through** belongs here; a stat that only changes **how big it is** does
 * not, because effective HP is measured per unit of raw incoming damage and scaling the hit
 * would answer a different question twice.
 *
 * So conversion, gain-as-extra, element damage, accuracy and every penetration are applied.
 * `total_damage` and the crit pair are not. `total_damage` comes back as `sizing`, for
 * {@link swingingSheet} and the over-time figures; crit is named in a diagnostic rather than
 * dropped silently.
 *
 * A map's `Mobs` affixes and tier are carried the same way and by the same rule: `fire_atk`'s
 * conversion and `all_fire_damage` shape the hit, the tier's `MORE total_damage` sizes it.
 */
function attackerAffixes(build, snapshot, index, bal, mobLevel, sheet, diagnostics) {
    const affixIds = build.config?.enemy?.affixes ?? [];
    const map = build.config?.map;
    if (affixIds.length === 0 && map === undefined)
        return { applied: [], withheld: [], sizing: [] };
    const report = (severity, code, path, message) => {
        diagnostics.push({ severity, code, path, message });
    };
    const applied = [];
    const withheld = [];
    const mods = [];
    const sizing = [];
    // The map's unknown ids are reported by the offensive pass; this one stays quiet about them so
    // a document is not told twice.
    const fromMap = mapMobMods(snapshot, index, bal, map, mobLevel, () => { }).map((m) => ({
        ...m,
        path: "config.map",
    }));
    const fromAffixes = mobAffixMods(snapshot, index, bal, mobLevel, affixIds, report).map((m) => ({
        ...m,
        path: "config.enemy.affixes",
    }));
    for (const mod of [...fromAffixes, ...fromMap]) {
        if (mod.statId === "total_damage") {
            sizing.push(mod);
            continue;
        }
        if (!shapesTheHit(mod.statId)) {
            if (sizesTheHit(mod.statId) && !withheld.includes(mod.statId))
                withheld.push(mod.statId);
            continue;
        }
        // Reported under the affix's own stat id and *applied* to whatever that id spreads to.
        // `penetrating` grants `elemental_penetration`, which no layer reads: the layers are per
        // single element, and the aggregate only reaches them through `ITransferToOtherStats` — a
        // pass this hand-built sheet never runs. Without the spread the affix moved your physical
        // and chaos figures (`armor_penetration` and `chaos_penetration` are real ids) and left fire,
        // cold and lightning untouched, which is not a mob that penetrates elements at all.
        if (!applied.includes(mod.statId))
            applied.push(mod.statId);
        for (const statId of aggregateSpread(mod.statId)) {
            mods.push({ ...mod, statId });
        }
    }
    applySheetMods(sheet, mods, index, report);
    return { applied, withheld, sizing };
}
/**
 * Conversion, gain-as-extra, element damage, accuracy and penetration — the families that decide
 * which of your layers a hit meets, and how hard it meets each.
 *
 * Matched by shape rather than listed, so an affix a pack adds with a new element lands without
 * an edit here: `phys_to_<element>`, `plus_phys_to_<element>`, `<element>_penetration`,
 * `all_<element>_damage`.
 */
function shapesTheHit(statId) {
    return (statId === "accuracy" ||
        statId === "armor_penetration" ||
        statId.endsWith("_penetration") ||
        statId.startsWith("phys_to_") ||
        statId.startsWith("plus_phys_to_") ||
        statId.startsWith("ele_to_") ||
        /^all_\w+_damage$/.test(statId));
}
/** The crit pair — real, and pinned off in every sweep here, so named rather than applied. */
function sizesTheHit(statId) {
    return statId === "critical_hit" || statId === "critical_damage";
}
function round(value) {
    return Math.round(value * 100) / 100;
}
/**
 * The chaos bypass, solved.
 *
 * Half of a chaos hit ignores the shield, so surviving `D` raw chaos means the shield absorbs
 * `min(D/2, S)` and health takes the rest. Below `H <= S` the binding constraint is the bypassing
 * half alone, giving `D = 2H`; above it the shield empties first and `D = H + S`. `min` of the two
 * is both arms at once.
 */
function poolFor(guid, health, shield, shieldHolds) {
    if (guid !== "chaos" || shieldHolds)
        return health + shield;
    return Math.min(health + shield, 2 * health);
}
/**
 * Whether the character has a shield in the offhand — `BlockChance`'s hardest gate.
 *
 *     if (!(effect.target.getOffhandItem().getItem() instanceof ShieldItem)) { return false; }
 *
 * The check is on the item class, which a build document does not carry, so the base's `shield`
 * tag stands in for it. Across the pack's three `offhand_family` bases that is exact rather than
 * approximate: `RoE_Weapons-0.1.7.jar` declares `Shield0Item extends ShieldItem`, while
 * `Tome0Item` and `Totem0Item` both extend plain `Item`. A tome grants magic shield and a totem
 * grants dodge; neither lets you block, and neither is a `ShieldItem`.
 *
 * A two-handed weapon empties the offhand outright — Better Combat returns `ItemStack.EMPTY`
 * for the slot, so `GearData` reads no offhand at all — which means a greatsword build holding
 * a shield blocks nothing. {@link isTwoHanded} is the same test `validate.ts` warns on.
 */
function wearsShield(build, snapshot) {
    const gear = wornItems(snapshot, build.gear ?? []);
    if (gear.some((item) => isTwoHanded(snapshot, item.base)))
        return false;
    return gear.some((item) => baseGearType(snapshot, item.base)?.tags.includes("shield") ?? false);
}
/**
 * One hit of `element`, swept against the character's own sheet.
 *
 * ## Converted damage is followed, not dropped
 *
 * `event.damage` is `EventData.NUMBER` alone, and both conversion paths *remove* their share
 * from it: `PhysicalDamageTakenAs` on your own sheet, and an attacker's `phys_to_fire`, each
 * subtract what they moved and park it on `event.bonusElements` as a child event. Reading
 * `NUMBER` on its own therefore counted converted damage as damage that never arrived, and a
 * character with `phys_taken_as_fire` and no fire resistance at all read as though half the hit
 * had been prevented rather than merely relabelled.
 *
 * So each child is swept in turn, at its own element and against its own mitigation layers,
 * exactly as `collectBonusElements` does on the offensive side. The two differences are the
 * ones the game makes: a `damage_taken_as` child sets `disableSourceStats`, because it was
 * built with `calcSourceEffects = false` and must not sweep the attacker a second time, and the
 * depth carries so `MAX_CONVERSION_DEPTH` still stops a phys→fire→phys pair from looping.
 */
function takenFraction(input) {
    const landed = new Map();
    const { dealt, steps } = sweepOnce(input, input.element, input.hitSize, 0, false, landed);
    const total = [...landed.values()].reduce((sum, n) => sum + n, 0);
    const arrivesAs = total <= 0
        ? []
        : [...landed.entries()]
            .filter(([, value]) => value > 0)
            .map(([element, value]) => ({ element, share: value / total }))
            .sort((a, b) => b.share - a.share);
    return { taken: input.hitSize > 0 ? dealt / input.hitSize : 1, steps, arrivesAs };
}
/** One event and every child it spawns, summed. */
function sweepOnce(input, element, amount, depth, takenAs, 
/**
 * Where each node's own contribution is tallied, by the element it arrived as.
 *
 * A map threaded down rather than merged back up, because the recursion already sums `dealt`
 * and a second accumulator returned alongside it would be two things to keep in step.
 */
landed) {
    const recorder = new Recorder();
    const event = new DamageEventState(input.layers, undefined, recorder);
    event.conversionDepth = depth;
    event.data.setupNumber(EVENT.NUMBER, amount);
    event.data.setString(EVENT.ELEMENT, element);
    event.data.setString(EVENT.ATTACK_TYPE, "hit");
    event.data.setBoolean(EVENT.CRIT, false);
    const ctx = {
        snapshot: input.snapshot,
        index: input.index,
        balance: input.balance,
        compat: input.compat,
        event,
        source: input.attacker,
        target: input.sheet,
        sourceLevel: input.attackerLevel,
        targetLevel: input.build.character.level,
        spell: undefined,
        spellId: "",
        spellTags: new Set(),
        config: input.build.config ?? {},
        effects: input.effects,
        diagnostics: input.diagnostics,
        report: (severity, code, path, message) => input.diagnostics.push({ severity, code, path, message }),
        reportedConditions: new Set(),
        reportedEffects: new Set(),
        // Crit is pinned off: eHP is per unit of raw damage, and a crit changes how big the raw
        // number is rather than how much of it you stop.
        pinnedBooleans: new Set([EVENT.CRIT]),
        // A `damage_taken_as` child is built with `calcSourceEffects = false`, so the attacker must
        // not be swept onto it a second time. A conversion child is not, and is.
        disableSourceStats: takenAs,
        sourceIsTarget: false,
        // The character is the target of every sweep in this file, so their own offhand answers
        // `BlockChance`'s shield gate.
        targetHasShield: input.hasShield,
        targetDualWielding: isDualWielding(input.snapshot, input.build.gear ?? []),
        ...(input.noAvoidance === true ? { noAvoidance: true } : {}),
        ...(input.procs === undefined ? {} : { procs: input.procs }),
        ...(input.restores === undefined ? {} : { restores: input.restores }),
    };
    const steps = [];
    const moreMultis = [];
    sweep(ctx, [
        { side: "Source", sheet: input.attacker },
        { side: "Target", sheet: input.sheet },
    ], steps, moreMultis);
    let dealt = Math.max(0, event.damage);
    landed.set(element, (landed.get(element) ?? 0) + dealt);
    // The ailments the hit puts on *you*. Same function, sheets swapped — `applyAilments` reads
    // `<ailment>_chance` off the source and `<ailment>_receive_chance` off the target, and here
    // the target is the character.
    //
    // No `runEvent`: the ailment's own second `DamageEvent` would need the offensive pipeline, so
    // these report the hit's base rather than the event's. That understates a DoT on a build with
    // `ailment_damage` — which is an *attacker* stat, and the attacker here is a bare mob — so the
    // gap is small and in the honest direction.
    if (input.ailments !== undefined) {
        input.ailments.push(...applyAilments(ctx, input.attacker, input.sheet));
    }
    // Everything conversion moved off `NUMBER`, followed to where it landed. The child's steps
    // are deliberately not merged into `steps`: the layer trace on screen is a reading of one
    // element's chain, and interleaving a second element's would make the column stop adding up.
    for (const bonus of event.bonusElements) {
        if (bonus.amount <= 0)
            continue;
        dealt += sweepOnce(input, bonus.element, bonus.amount, depth + 1, bonus.takenAs, landed).dealt;
    }
    return { dealt, steps };
}
//# sourceMappingURL=defence.js.map